import type { BibleBook } from "./types";

/** Matthew to Acts — four accounts of Jesus and one of what happened next. */
export const GOSPELS: BibleBook[] = [
  {
    slug: "matthew",
    name: "Matthew",
    testament: "new",
    genre: "gospel",
    order: 40,
    chapters: 28,
    author: "Traditionally Matthew the tax collector; formally anonymous",
    written: "Probably 80s AD, possibly earlier",
    audience: "Jewish Christians arguing about continuity with Israel",
    argument:
      "The gospel written to show that nothing has been abandoned — organised around five blocks of teaching, in deliberate echo of the five books of Moses.",
    summary: [
      "Matthew opens with a genealogy, which is a strange way to begin a biography and an obvious one if your argument is that this man belongs in Israel's story. It names four women, three of them foreign or scandalous, which is not an accident.",
      "The book is structured around five long teaching blocks, of which the Sermon on the Mount is the first and by far the best known. That structure is widely read as an echo of the Pentateuch: a new Moses giving instruction from a mountain.",
      "It quotes the Old Testament more than any other gospel and repeatedly says that something happened to fulfil what was written, which is the book's central move. It ends with the commission to go to all nations, which for a gospel this concerned with Israel is the point rather than a coda.",
    ],
    startHere:
      "Chapter 5. The Sermon on the Mount runs to the end of chapter 7 and is the densest three chapters in the New Testament.",
    themes: ["fulfilment", "the kingdom", "teaching", "the nations"],
    keyChapters: [
      {
        chapter: 5,
        why: "The Beatitudes and the start of the Sermon on the Mount, which runs to the end of chapter 7.",
      },
      {
        chapter: 13,
        why: "Seven parables of the kingdom in one block, including the sower and the pearl.",
      },
      {
        chapter: 25,
        why: "The sheep and the goats, where the criterion is what was done for the hungry and imprisoned.",
      },
    ],
    known: {
      text: "Go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit,",
      ref: "Matthew 28:19",
    },
    misread:
      "That the Sermon on the Mount is a set of ideals nobody could keep. Matthew presents it as instruction to be done — the block ends with a parable about hearing these words and acting on them or not.",
    faq: [
      {
        q: "Why does Matthew start with a genealogy?",
        a: "Because his argument is continuity: this man belongs in Israel's story. The list runs from Abraham through David, and it names four women whose inclusion would have surprised a first-century reader.",
      },
      {
        q: "What is the Sermon on the Mount?",
        a: "Matthew chapters 5 to 7, the first of the book's five teaching blocks. It contains the Beatitudes, the Lord's Prayer and the instruction about not worrying, and it ends by insisting the words are for doing.",
      },
    ],
    passages: ["matthew-6-33", "matthew-11-28-30"],
    topics: ["bible-verses-about-forgiveness"],
  },
  {
    slug: "mark",
    name: "Mark",
    testament: "new",
    genre: "gospel",
    order: 41,
    chapters: 16,
    author: "Traditionally Mark, associated with Peter; formally anonymous",
    written: "Probably around 70 AD; generally held to be the earliest gospel",
    audience: "A church under pressure, possibly in Rome",
    argument:
      "The shortest, fastest gospel, which spends a third of its length on one week and ends — in the oldest manuscripts — with frightened women saying nothing to anyone.",
    summary: [
      "Mark moves. The word usually translated immediately appears more than forty times, and the first half reads as a series of rapid scenes with almost no discourse. Nearly every scholar considers it the earliest of the four and a source for Matthew and Luke.",
      "It is also the most reticent. Jesus repeatedly tells people not to say who he is, the disciples consistently fail to understand, and the first human being to state the identity correctly is the Roman centurion at the execution.",
      "The ending is the most discussed textual problem in the New Testament. The oldest manuscripts stop at 16:8, with the women fleeing the empty tomb in terror and telling nobody. The longer ending in most Bibles is a later addition, and the abrupt version is almost certainly what Mark wrote.",
    ],
    startHere:
      "Chapter 1, and read the whole book in a sitting. It takes about ninety minutes and is designed to be taken that way.",
    themes: ["urgency", "secrecy", "service", "the cross"],
    keyChapters: [
      {
        chapter: 4,
        why: "The sower and the storm, and the disciples asking whether he cares that they are drowning.",
      },
      {
        chapter: 8,
        why: "The hinge of the book — Peter's confession, and the first prediction of the cross.",
      },
      {
        chapter: 15,
        why: "The crucifixion, and the centurion who is the first human being in the book to say who he is.",
      },
    ],
    known: {
      text: "For the Son of Man also came not to be served but to serve, and to give his life as a ransom for many.",
      ref: "Mark 10:45",
    },
    misread:
      "That Mark is an abbreviated version of Matthew. It is almost certainly the earliest, and its brevity is a style rather than a summary — its scenes are frequently more detailed than the parallel accounts.",
    faq: [
      {
        q: "Why does Mark end so abruptly?",
        a: "The oldest manuscripts end at 16:8 with the women afraid and silent. The longer ending found in many Bibles is a later addition, and most scholars think the abrupt ending is original and deliberate.",
      },
      {
        q: "Was Mark the first gospel written?",
        a: "Most scholars think so, dating it around 70 AD and treating it as a source used by Matthew and Luke — a view based on the amount of Mark that appears, often word for word, in both.",
      },
    ],
  },
  {
    slug: "luke",
    name: "Luke",
    testament: "new",
    genre: "gospel",
    order: 42,
    chapters: 24,
    author: "Traditionally Luke, a physician and companion of Paul",
    written: "Probably 80s AD",
    audience: "Theophilus, and a largely non-Jewish readership",
    argument:
      "The gospel that keeps putting the wrong people at the centre — shepherds, a Samaritan, a tax collector, women — and the first half of a two-volume work.",
    summary: [
      "Luke opens like a historical work, addressed to a named individual and stating a method: an orderly account based on eyewitnesses. It is the longest of the four gospels and the first volume of a two-part work continued in Acts.",
      "Its distinctive material is what makes it the most-quoted gospel in general culture. The nativity with shepherds and a manger, the good Samaritan, the prodigal son, the rich man and Lazarus — none of these appear anywhere else.",
      "The pattern in that material is consistent: the person expected to be marginal is the one who acts rightly, and the respectable one does not. Luke also gives more attention to women, to the poor and to prayer than the other gospels.",
    ],
    startHere:
      "Chapter 15, three parables about losing and finding, of which the third is the prodigal son.",
    themes: ["the outsider", "prayer", "reversal", "the poor"],
    keyChapters: [
      {
        chapter: 10,
        why: "The good Samaritan, told to a lawyer trying to narrow the definition of neighbour.",
      },
      {
        chapter: 15,
        why: "Three parables about losing and finding, ending with an older brother who will not come in.",
      },
      {
        chapter: 24,
        why: "The road to Emmaus, where he explains the whole thing to two people who do not recognise him.",
      },
    ],
    known: {
      text: "For the Son of Man came to seek and to save that which was lost.",
      ref: "Luke 19:10",
    },
    misread:
      "That the prodigal son is about the younger brother. The parable has a third act about the older one, who refuses to come in, and it is left unresolved — because Luke's audience for the story were the respectable people complaining.",
    faq: [
      {
        q: "What is unique about Luke's gospel?",
        a: "Much of its best-known material appears nowhere else: the shepherds at the nativity, the good Samaritan, the prodigal son. It gives unusual attention to women, the poor and prayer.",
      },
      {
        q: "Did Luke write Acts?",
        a: "Both are addressed to Theophilus and Acts explicitly refers back to a former book, so they are almost universally treated as two volumes of one work by the same author.",
      },
    ],
  },
  {
    slug: "john",
    name: "John",
    testament: "new",
    genre: "gospel",
    order: 43,
    chapters: 21,
    author: "Traditionally John the apostle; formally anonymous",
    written: "Probably 90s AD",
    audience: "A community in conflict with the synagogue it came from",
    argument:
      "The gospel that shares almost no material with the other three, replaces parables with long discourses, and states its purpose explicitly in its second-to-last paragraph.",
    summary: [
      "John is different from the first three in almost every respect. It has no birth narrative, no parables, no exorcisms and no last supper institution. Instead it has seven signs, seven I am sayings, and discourses that run for chapters.",
      "It opens not with a genealogy or a manger but before creation, and the prologue is the densest theological writing in the New Testament. Its miracles are called signs and each one is interpreted at length.",
      "It also tells you what it is for: these are written that you may believe. That is an unusually explicit statement of purpose for an ancient book, and it explains the selection.",
    ],
    startHere:
      "Chapter 1 for the prologue, then chapter 11, the raising of Lazarus, which is the last and largest of the signs.",
    themes: ["signs", "glory", "belief", "abiding"],
    keyChapters: [
      {
        chapter: 1,
        why: "The prologue, which starts before creation rather than in Bethlehem.",
      },
      {
        chapter: 11,
        why: "The raising of Lazarus, the last and largest sign, and the shortest verse in the Bible.",
      },
      {
        chapter: 17,
        why: "The prayer before the arrest, which is the longest recorded prayer of Jesus.",
      },
    ],
    known: {
      text: "For God so loved the world, that he gave his only born Son, that whoever believes in him should not perish, but have eternal life.",
      ref: "John 3:16",
    },
    misread:
      "That John is a devotional supplement to the historical three. It is making a sustained argument, and its differences from the others are structural rather than decorative.",
    faq: [
      {
        q: "Why is John so different from the other gospels?",
        a: "It shares little material with them, replaces parables with extended discourses, and organises itself around seven signs. It was written later and to a community in a specific conflict, and it states its persuasive purpose openly.",
      },
      {
        q: "What are the I am sayings in John?",
        a: "Seven statements — the bread of life, the light of the world, the door, the good shepherd, the resurrection and the life, the way, the true vine — each attached to a sign or a discourse.",
      },
    ],
    passages: ["john-3-16"],
  },
  {
    slug: "acts",
    name: "Acts",
    testament: "new",
    genre: "history",
    order: 44,
    chapters: 28,
    author: "The author of Luke",
    written: "Probably 80s AD",
    audience: "Theophilus again, and a church working out what it is",
    argument:
      "The second volume of Luke, tracking a Jewish renewal movement becoming a Gentile church, and ending mid-sentence with Paul under house arrest.",
    summary: [
      "Acts follows the church from Jerusalem outward, and its structure follows the programme stated in its first chapter: Jerusalem, Judea, Samaria, and the ends of the earth. The first half belongs to Peter, the second to Paul.",
      "The central conflict is not persecution but inclusion. Chapter 10, with Peter and Cornelius, and chapter 15, the council in Jerusalem, are the hinges: can non-Jews belong without becoming Jews first? The answer transforms the movement.",
      "It ends without an ending. Paul is under house arrest in Rome, preaching without hindrance, and the book stops. Whether that is a literary choice or the point at which the author was writing has been argued for centuries.",
    ],
    startHere:
      "Chapter 2 for Pentecost, then chapter 15 for the council that decided what the church would be.",
    themes: ["the Spirit", "expansion", "inclusion", "witness"],
    keyChapters: [
      {
        chapter: 2,
        why: "Pentecost, and the sermon that quotes Joel to explain what is happening.",
      },
      {
        chapter: 10,
        why: "Peter and Cornelius, where the question of Gentile inclusion is forced by a vision.",
      },
      {
        chapter: 15,
        why: "The Jerusalem council, which decides what the church will be.",
      },
    ],
    known: {
      text: "But you will receive power when the Holy Spirit has come upon you. You will be witnesses to me in Jerusalem, in all Judea and Samaria, and to the uttermost parts of the earth.",
      ref: "Acts 1:8",
    },
    misread:
      "That it is a book about miracles and preaching. Its main argument is about membership — whether Gentiles could belong without becoming Jews — and that question drives most of its plot.",
    faq: [
      {
        q: "What is the book of Acts about?",
        a: "The first decades of the church, from Pentecost to Paul in Rome, organised around the spread from Jerusalem outward and the question of whether Gentiles could belong without converting to Judaism first.",
      },
      {
        q: "Why does Acts end so suddenly?",
        a: "Paul is under house arrest and the narrative simply stops. Explanations range from the author writing at that moment to a deliberate ending on an unfinished mission; the text gives no signal either way.",
      },
    ],
  },
];
